import type { Editor } from "@tiptap/react";
import type { ChatFileAttachment } from "@v2/features/chat/utils/chatFileAttachment";
import type { ToolResultMessageContent } from "schemas/chat";
import type {
  ChatMessage,
  ChatMessageContent,
} from "schemas/generated/chatMessage";

import React, { useCallback, useEffect, useRef, useState } from "react";

import { useIsMobile } from "@replo/design-system/hooks/use-mobile";
import twMerge from "@replo/design-system/utils/twMerge";
import { skipToken } from "@tanstack/react-query";
import Placeholder from "@tiptap/extension-placeholder";
import { useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { Chat } from "@v2/features/chat/components/Chat";
import ChatHeader from "@v2/features/chat/components/chat-elements/ChatHeader";
import ManageChats from "@v2/features/chat/components/chat-elements/ManageChats";
import AssetComponent from "@v2/features/chat/components/chat-elements/message-input/assetNodeExtension";
import IntegrationComponent from "@v2/features/chat/components/chat-elements/message-input/integrationNodeExtension";
import ProductComponent from "@v2/features/chat/components/chat-elements/message-input/productNodeExtension";
import useChatMessageLimits from "@v2/features/chat/hooks/useChatMessageLimits";
import { useChatTurn } from "@v2/features/chat/hooks/useChatTurn";
import { useCurrentChatContext } from "@v2/features/chat/providers/CurrentChatContext";
import { useActiveTriggerJobsForChat } from "@v2/features/chat/store/ChatStoreContext";
import { useDefaultSelectedElementDesignation } from "@v2/features/modifiers/store/ModifiersStoreContext";
import useCurrentProjectId from "@v2/hooks/useCurrentProjectId";
import { useLocalStorage } from "@v2/hooks/useLocalStorage";
import { useLogAnalytics } from "@v2/hooks/useLogAnalytics";
import { chatRouter, chatUtils } from "@v2/trpc/routes/chat";
import { marketingClient } from "@v2/trpc/routes/marketing";
import { triggerRouter } from "@v2/trpc/routes/trigger";
import { workspaceUtils } from "@v2/trpc/routes/workspace";
import { useLocation, useSearchParams } from "react-router-dom";
import { useRequiredContext } from "replo-utils/react/context";
import { Markdown as TiptapMarkdown } from "tiptap-markdown";
import { v4 } from "uuid";

import HardBreakMarkdown from "../extensions/hardBreakMarkdown";
import { useChatSidebar } from "../hooks/useChatSidebar";
import {
  useChatStoreActions,
  useIsChatLoading,
  useMessagesForChat,
} from "../store/ChatStoreContext";
import {
  chatEditorHandleKeyDown,
  chatEditorHandlePaste,
  getCleanedChatPrompt,
  useChatEditorOnUpdate,
} from "../utils/tiptapUtils";

const useChatAutoSubmitListeners = () => {
  const [autoSubmitData, setAutoSubmitData] = useState<{
    prompt: string;
    shouldSubmit: boolean;
    isSubmitting?: boolean;
  } | null>(null);
  const logEvent = useLogAnalytics();
  const [, setSearchParams] = useSearchParams();
  const { setFiles, addFiles, files, chatId, sendChatRequest } =
    useCurrentChatContext();
  const location = useLocation();
  const { editor: chatEditor } = useRequiredContext(ChatEditorContext);

  const storedData = localStorage.getItem("replo_marketing_seed");
  if (storedData) {
    const marketingSeedData = JSON.parse(storedData);
    if (marketingSeedData?.prompt) {
      logEvent("marketing.site.prompt", {
        prompt: marketingSeedData.prompt,
        jti: marketingSeedData.jti,
        hasImage: Boolean(marketingSeedData.hasImage),
      });

      setSearchParams((prev) => {
        const newParams = new URLSearchParams(prev);
        newParams.set("chatId", "");
        return newParams;
      });
      if (marketingSeedData?.jti && marketingSeedData?.hasImage) {
        void (async () => {
          const { blob: base64Blob, contentType } =
            await marketingClient.getMarketingSeedImage.query({
              jti: marketingSeedData.jti,
            });
          const binaryString = atob(base64Blob);
          const bytes = new Uint8Array(binaryString.length);
          for (let i = 0; i < binaryString.length; i++) {
            bytes[i] = binaryString.charCodeAt(i);
          }
          const blob = new Blob([bytes], { type: contentType });
          const file = new File([blob], "marketing-seed-image", {
            type: contentType,
          });
          await addFiles([file]);

          setAutoSubmitData({
            prompt: marketingSeedData.prompt,
            shouldSubmit: true,
          });
        })();
      } else {
        setAutoSubmitData({
          prompt: marketingSeedData.prompt,
          shouldSubmit: true,
        });
      }

      localStorage.removeItem("replo_marketing_seed");
    }
  }

  // Handle initial message from navigation state
  useEffect(() => {
    const initialData = location.state as {
      initialMessage?: string;
      initialFiles?: ChatFileAttachment[] | null;
    } | null;

    if (initialData?.initialMessage && !chatId) {
      if (initialData.initialFiles) {
        setFiles(initialData.initialFiles);
      }

      setAutoSubmitData({
        prompt: initialData.initialMessage,
        shouldSubmit: true,
      });

      window.history.replaceState(
        {},
        document.title,
        window.location.pathname + window.location.search,
      );
    }
  }, [location.state, chatId, setFiles]);

  // NOTE (Bobby, 2025-07-30): Have this as a separate useEffect since we need to wait for the files state to be set before submitting.
  // NOTE (Kevin, 2025-08-25): sendChatRequest is intentionally not included as a dependency because autoSubmit useEffect
  // had sendChatRequest as a dependency, which was being recreated on every render, causing an infinite loop.
  // We mark as submitting to prevent re-triggering while preserving data for retry on failure
  // biome-ignore lint/correctness/useExhaustiveDependencies: we want to prevent infinite loops
  useEffect(() => {
    if (
      autoSubmitData?.shouldSubmit &&
      !autoSubmitData.isSubmitting &&
      chatEditor
    ) {
      const prompt = autoSubmitData.prompt;
      setAutoSubmitData({ prompt, shouldSubmit: true, isSubmitting: true });

      const handleAutoSubmit = async () => {
        try {
          await sendChatRequest({
            prompt,
            isHidden: false,
            filesToUpload: files,
          });
          // NOTE (Ben, 2025-12-12): Removed chatEditor.commands.setContent("", false) here.
          // The auto-submitted prompt comes from navigation state, not the editor, so clearing
          // the editor after streaming completes would wipe any text the user typed while waiting.
          setFiles(null);
          setAutoSubmitData(null);
        } catch {
          setAutoSubmitData({
            prompt,
            shouldSubmit: true,
            isSubmitting: false,
          });
        }
      };

      void handleAutoSubmit();
      setFiles(null);
    }
  }, [autoSubmitData, chatEditor]);
};

const ChatAutoSubmitListener = () => {
  useChatAutoSubmitListeners();
  return null;
};

export const ChatEditorContext = React.createContext<{
  editor: Editor | null;
}>({
  editor: null,
});

const ChatEditorProvider = ({
  children,
  onSubmit,
}: React.PropsWithChildren<{
  onSubmit: (chatEditor: Editor) => void;
}>) => {
  const { addFiles } = useCurrentChatContext();
  const chatEditorOnUpdate = useChatEditorOnUpdate();
  const chatEditor = useEditor({
    extensions: [
      StarterKit.configure({
        hardBreak: false,
      }),
      AssetComponent,
      ProductComponent,
      IntegrationComponent,
      HardBreakMarkdown,
      TiptapMarkdown.configure({
        /*
         * NOTE (Kevin 2025-09-24):
         * breaks=false because the HardBreak extension emits <br /> directly.
         * html=true keeps <chat-token> placeholders intact through markdown.
         * tightLists=true prevents blank paragraphs between list items.
         */
        breaks: false,
        html: true,
        tightLists: true,
      }),
      Placeholder.configure({
        placeholder: "Ask for any edits or customizations",
      }),
    ],
    content: "",
    onUpdate: chatEditorOnUpdate,
    onCreate: ({ editor }) => {
      editor.commands.focus();
    },
    editorProps: {
      attributes: {
        style: "font-size: 14px; white-space: pre-wrap;",
        class: "react-editor-chat",
      },
      handleKeyDown: chatEditorHandleKeyDown(() => {
        if (!chatEditor) {
          return;
        }
        onSubmit(chatEditor);
      }),
      handlePaste: chatEditorHandlePaste(addFiles),
    },
  });

  return (
    <ChatEditorContext.Provider value={{ editor: chatEditor }}>
      {children}
    </ChatEditorContext.Provider>
  );
};

export default function ReactEditorChat() {
  const location = useLocation();
  const projectId = useCurrentProjectId();

  const [searchParams, setSearchParams] = useSearchParams();
  const pageId = searchParams.get("pageId");

  const { sendChatRequest, chatId, files, setFiles, chatError } =
    useCurrentChatContext();

  const defaultDesignation = useDefaultSelectedElementDesignation();
  const { messagesRemaining, hasUnlimitedMessages } = useChatMessageLimits();

  const { getChatTurnId } = useChatTurn();

  const { open: isChatSidebarOpen, setOpen: setIsChatSidebarOpen } =
    useChatSidebar();
  const isMobile = useIsMobile();

  const isChatLoading = useIsChatLoading(chatId);
  const {
    getStreamingState,
    addOrUpdateMessageForChat,
    syncMessagesForChat,
    updateStreamingStatus,
    removeActiveTriggerJob,
  } = useChatStoreActions();

  const activeTriggerJobs = useActiveTriggerJobsForChat(chatId);

  const storage = useLocalStorage();

  const allMessages = useMessagesForChat(chatId);

  const { mutate: cancelRun } = triggerRouter.cancelRun.useMutation({});
  const { mutate: cancelChatTurn } = chatRouter.cancelChatTurn.useMutation({
    onSuccess: () => {
      void chatUtils.getPaginatedMessages.invalidate({
        chatId: chatId ?? undefined,
      });
    },
  });

  const {
    data: paginatedMessagesData,
    fetchNextPage,
    hasNextPage,
  } = chatRouter.getPaginatedMessages.useInfiniteQuery(
    chatId ? { chatId, limit: 50 } : skipToken,
    {
      getNextPageParam: (lastPage) => lastPage.nextCursor,
      staleTime: 0,
    },
  );

  const paginatedMessages = React.useMemo(() => {
    if (!paginatedMessagesData?.pages) {
      return [];
    }

    // Reverse pages so older pages (fetched later) come first
    const allMessages = paginatedMessagesData.pages
      .slice()
      .reverse()
      .flatMap((page) => page.messages);

    return allMessages;
  }, [paginatedMessagesData]);

  useEffect(() => {
    if (chatId && paginatedMessages.length > 0) {
      syncMessagesForChat(chatId, paginatedMessages as ChatMessage[]);
    }
  }, [paginatedMessages, syncMessagesForChat, chatId]);

  // NOTE (Ben, 2025-12-05): This ref is populated by Chat.tsx with a submit handler that includes
  // scroll-to-bottom behavior. We need this because there are two submit paths:
  // 1. Enter key → ChatEditorProvider.onSubmit (bypasses MessageInput's form)
  // 2. Button click → MessageInput's form onSubmit
  // The ref allows both paths to use the same scroll-enabled handler defined in Chat.tsx.
  const onSubmitRef = useRef<
    | ((opts: { chatEditor: Editor; event: React.FormEvent | null }) => void)
    | null
  >(null);

  const handleSubmit = async (opts: {
    chatEditor: Editor;
    event: React.FormEvent | null;
  }) => {
    const { chatEditor, event } = opts;
    event?.preventDefault();

    if (!chatEditor || isChatLoading || activeTriggerJobs.length > 0) {
      return;
    }

    if (!hasUnlimitedMessages && messagesRemaining <= 0) {
      return;
    }
    const currentPrompt = getCleanedChatPrompt(chatEditor);
    chatEditor.commands.setContent("", false);
    setFiles(null);

    setIsChatSidebarOpen(false);

    await sendChatRequest({
      prompt: currentPrompt,
      isHidden: false,
      defaultDesignation,
      filesToUpload: files,
    });
  };

  const handleStop = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
      e.stopPropagation();

      if (chatId) {
        const streamingState = getStreamingState(chatId);

        if (!streamingState.isStreaming) {
          return;
        }

        const chatTurnId = getChatTurnId();

        cancelChatTurn({ chatTurnId });

        if (activeTriggerJobs.length > 0) {
          for (const job of activeTriggerJobs) {
            void cancelRun({ runId: job.jobId });
            removeActiveTriggerJob(chatId, job.jobId);
          }
        }

        updateStreamingStatus(chatId, false);

        // NOTE (Kurt, 2025-08-12): We add a synthetic tool-result to the chat optimistically
        // to show the user the cancelled ChangeBlock. We do this because when the `handleStop`
        // function is invoked, it takes a non-deterministic amount of time to add the tool-result
        // to the `chat_messages` table. Instead of waiting for the tool-result or add a setTimeout,
        // we just add this message, which no longer exists once the latests chat message are fetched.
        const canceledContent = buildCanceledToolResultContent(allMessages);
        if (canceledContent) {
          addOrUpdateMessageForChat(
            chatId,
            v4(),
            canceledContent,
            "tool",
            chatTurnId,
          );
        }

        // Invalidate chat messages usage when user stops the chat early to ensure accurate counts
        void workspaceUtils.getChatMessagesUsage.invalidate();
      }
    },
    [
      chatId,
      getStreamingState,
      cancelChatTurn,
      updateStreamingStatus,
      allMessages,
      addOrUpdateMessageForChat,
      activeTriggerJobs,
      removeActiveTriggerJob,
      cancelRun,
      getChatTurnId,
    ],
  );

  // Find most recent chat for this page if needed
  const { data: mostRecentChatForPageData } =
    chatRouter.getPaginatedChats.useQuery(
      chatId === null && pageId && projectId
        ? { projectId, pageId, limit: 1 }
        : skipToken,
      {
        staleTime: 0,
      },
    );

  const mostRecentChatForPage = mostRecentChatForPageData?.chats[0];

  useEffect(() => {
    if (location.state?.initialMessage && !chatId) {
      return;
    }

    if (chatError && chatId && projectId && pageId) {
      setSearchParams((prev) => {
        const newParams = new URLSearchParams(prev);
        newParams.set("chatId", "");
        return newParams;
      });
    }
  }, [chatError, chatId, projectId, pageId, setSearchParams, location.state]);

  // NOTE (Kevin, 2025-08-21): Navigation logic for what happens when the user navigates to the chat page
  // from inputting a message or clicking on a page in Home or navigating to Chat via the sidebar.
  // P1: Never redirect when there's an initial message
  // P2: Restore last viewed chat from localStorage (when !pageId)
  // P3: Restore last chat for this page from database (when pageId but no chatId)
  // Also handles saving current chatId to localStorage
  useEffect(() => {
    if (location.state?.initialMessage && !chatId) {
      return;
    }

    // Restore last chat for this specific page from database
    if (
      chatId === null &&
      pageId &&
      projectId &&
      mostRecentChatForPage &&
      !location.state?.initialMessage
    ) {
      setSearchParams((prev) => {
        const newParams = new URLSearchParams(prev);
        newParams.set("chatId", mostRecentChatForPage.id);
        return newParams;
      });
      return;
    }

    // Restore last viewed chat from localStorage (when no specific page)
    if (
      chatId === null &&
      !pageId &&
      projectId &&
      !location.state?.initialMessage
    ) {
      const lastChatId = storage.getItem(
        `replo.editor.lastChatId.${projectId}`,
      );
      if (lastChatId) {
        setSearchParams((prev) => {
          const newParams = new URLSearchParams(prev);
          newParams.set("chatId", lastChatId);
          return newParams;
        });
        return;
      }
    }

    if (chatId && projectId) {
      storage.setItem(`replo.editor.lastChatId.${projectId}`, chatId);
    }
  }, [
    chatId,
    pageId,
    projectId,
    setSearchParams,
    location.state,
    storage,
    mostRecentChatForPage,
  ]);

  return (
    <div className="flex w-full h-full min-h-0 flex-row shadow-2xs border-0 sm:border-0.5 border-border sm:rounded-lg bg-white relative overflow-visible">
      <style>
        {`
          .react-editor-chat.tiptap p.is-editor-empty:first-child::before {
            color: var(--color-neutral-500);
            content: attr(data-placeholder);
            float: left;
            height: 0;
            pointer-events: none;
          }
          .react-editor-chat.tiptap.ProseMirror-focused p.is-editor-empty:first-child::before {
            color: var(--color-neutral-400);
          }
          .react-editor-chat.tiptap ol {
            list-style-type: decimal;
            list-style-position: outside;
            padding-left: 1.5rem;
          }
          .react-editor-chat.tiptap ul {
            list-style-type: disc;
            list-style-position: outside;
            padding-left: 1.5rem;
          }
        `}
      </style>

      {/* Left Chat List (desktop only; mobile handled by layout-level dock) */}
      {!isMobile && (
        <div
          data-state={isChatSidebarOpen ? "expanded" : "collapsed"}
          aria-hidden={!isChatSidebarOpen}
          className={twMerge(
            "flex h-full flex-shrink-0 flex-col overflow-hidden transition-[width,opacity] duration-300 ease-out data-[state=collapsed]:w-0 data-[state=collapsed]:opacity-0",
            "w-[250px] border-r-0.5 border-border rounded-tl-lg rounded-bl-lg",
            !isChatSidebarOpen && "border-r-transparent",
          )}
        >
          <div className="flex flex-col min-w-[250px] h-full">
            <ManageChats />
          </div>
        </div>
      )}

      {/* Main Chat Panel */}
      <div className="flex-1 flex flex-col h-full min-h-0 items-center overflow-hidden">
        <div className="hidden md:flex w-full justify-start p-3 bg-white rounded-t-lg border-b-0.5 border-border min-h-14">
          <ChatHeader />
        </div>
        <div className="p-3 flex flex-col gap-4 h-full min-h-0 max-w-full sm:max-w-2xl lg:max-w-3xl w-full relative overflow-hidden">
          <ChatEditorProvider
            onSubmit={(editor) => {
              const submitHandler = onSubmitRef.current ?? handleSubmit;
              void submitHandler({ chatEditor: editor, event: null });
            }}
          >
            <Chat
              handleSubmit={(opts) => void handleSubmit(opts)}
              stop={handleStop}
              hasNextPage={hasNextPage ?? false}
              fetchNextPage={() => void fetchNextPage()}
              onSubmitRef={onSubmitRef}
            />
            <ChatAutoSubmitListener />
          </ChatEditorProvider>
        </div>
      </div>
    </div>
  );
}

/**
 * NOTE (Kurt, 2025-08-12): When the user presses Stop, this builds a synthetic canceled
 * 'tool-result' for the most recent in-flight assistant tool-call so the UI can close
 * the active card and we can optimistically show the user the cancelled ChangeBlock.
 *
 * We pick the last tool-call in the latest assistant message because one assistant
 * message can include multiple calls and the newest is the one still running.
 *
 * We must reuse the original toolCallId/toolName so rendering can link result→call;
 * otherwise the result is orphaned and the chat doesn't know where in the chat to put
 * this result.
 */
function buildCanceledToolResultContent(
  allMessages: ChatMessage[],
): ChatMessageContent | null {
  const lastAssistantWithToolCall = allMessages.findLast((message) => {
    return (
      message.role === "assistant" &&
      message.content.some((contentItem) => contentItem.type === "tool-call")
    );
  });

  if (!lastAssistantWithToolCall) {
    return null;
  }

  const toolCalls = lastAssistantWithToolCall.content.filter(
    (contentItem) => contentItem.type === "tool-call",
  );

  const lastToolCall = toolCalls.findLast(() => true);
  if (!lastToolCall) {
    return null;
  }

  const toolName =
    lastToolCall.toolName as ToolResultMessageContent["toolName"];

  return [
    {
      type: "tool-result",
      toolCallId: lastToolCall.toolCallId,
      toolName,
      output: {
        type: "json",
        value: { success: false, isCanceled: true },
      },
    },
  ];
}
